<script setup lang="ts">
import { useAuthStore } from '../stores/auth'
import { useBookingsStore } from '../stores/bookings'
import { computed } from 'vue'

const authStore = useAuthStore()
const bookingsStore = useBookingsStore()

const pendingCount = computed(() => bookingsStore.pendingBookings.length)
</script>

<template>
  <nav class="bg-primary-700 text-white shadow-nav">
    <div class="max-w-7xl mx-auto px-4">
      <div class="flex items-center justify-between h-16">
        <!-- Logo -->
        <router-link to="/" class="flex items-center gap-2 font-bold text-xl group">
          <svg class="w-7 h-7 transition-transform duration-200 group-hover:scale-110" viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <path d="M32 8L4 32h8v20h36V32h8L32 8z" fill="white" opacity="0.9"/>
            <rect x="26" y="36" width="12" height="16" rx="1" fill="#d4a574" opacity="0.7"/>
            <rect x="40" y="12" width="5" height="14" fill="white" opacity="0.5"/>
          </svg>
          <span class="hidden sm:inline">Vermont Cabin</span>
        </router-link>

        <!-- Navigation Links -->
        <div class="flex items-center gap-1 md:gap-4">
          <router-link
            to="/"
            class="px-2 md:px-3 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm md:text-base"
          >
            <span class="hidden md:inline">Conditions</span>
            <span class="md:hidden">🌡️</span>
          </router-link>

          <router-link
            to="/calendar"
            class="px-2 md:px-3 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm md:text-base"
          >
            Calendar
          </router-link>

          <router-link
            to="/guide"
            class="px-2 md:px-3 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm md:text-base"
          >
            <span class="hidden md:inline">Guide</span>
            <span class="md:hidden">📋</span>
          </router-link>

          <router-link
            to="/completion"
            class="px-2 md:px-3 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm md:text-base"
          >
            <span class="hidden md:inline">Progress</span>
            <span class="md:hidden">🏗️</span>
          </router-link>

          <router-link
            v-if="authStore.canViewInventory"
            to="/inventory"
            class="px-2 md:px-3 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm md:text-base"
          >
            <span class="hidden md:inline">Inventory</span>
            <span class="md:hidden">📦</span>
          </router-link>

          <router-link
            to="/consumables"
            class="px-2 md:px-3 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm md:text-base"
          >
            <span class="hidden md:inline">Supplies</span>
            <span class="md:hidden">🧴</span>
          </router-link>

          <router-link
            v-if="authStore.canManageInventory || authStore.user?.role === 'family' || authStore.user?.role === 'friend'"
            to="/checklists"
            class="px-2 md:px-3 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm md:text-base"
          >
            <span class="hidden md:inline">Checklists</span>
            <span class="md:hidden">✅</span>
          </router-link>

          <router-link
            v-if="authStore.canBook"
            to="/request"
            class="px-2 md:px-3 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm md:text-base"
          >
            <span class="hidden md:inline">Book Stay</span>
            <span class="md:hidden">📅</span>
          </router-link>

          <router-link
            v-if="authStore.isOwner"
            to="/admin"
            class="px-2 md:px-3 py-2 rounded-lg hover:bg-primary-600 transition-colors relative text-sm md:text-base"
          >
            Admin
            <span
              v-if="pendingCount > 0"
              class="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center"
            >
              {{ pendingCount }}
            </span>
          </router-link>
        </div>

        <!-- User Menu -->
        <div class="flex items-center gap-3">
          <a
            href="mailto:support@macris.me?subject=Cabin Calendar Issue"
            class="hidden md:inline text-xs text-primary-200 hover:text-white transition-colors"
          >
            Report Issue
          </a>
          <img
            v-if="authStore.user?.picture_url"
            :src="authStore.user.picture_url"
            :alt="authStore.user.name"
            class="w-8 h-8 rounded-full"
          />
          <span class="hidden md:inline text-sm">{{ authStore.user?.name }}</span>
          <button
            @click="authStore.logout"
            class="text-sm px-3 py-1 rounded-lg bg-primary-600 hover:bg-primary-500 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>
